import { type Capability, entryKeys, loanKeys } from '@taler/api-client'
import { defineEntryExtension, type RegisteredExtension } from '@/features/entries'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useApi } from '@/app/api'
import LoanPaymentRowDetail from '@/features/loans/components/LoanPaymentRowDetail.vue'
import { canViewLoans } from '../internal/capabilities.ts'
import { loanAccountsById } from '../internal/loan-accounts.ts'
import type { Loan, LoanPayment } from '../internal/types.ts'

export interface UseLoanPaymentExtensionOptions {
  readonly ledgerId: () => string | undefined
  readonly can: (capability: Capability) => boolean
}

export interface LoanRowMatch {
  readonly key: string
  readonly label: string
  /** `payment` routes the row to the loan; the other two can only be refused. */
  readonly mode: 'payment' | 'borrowing' | 'interest'
  readonly loan: Loan
}

export interface LoanPaymentDraft {
  readonly posted: LoanPayment | undefined
}

/** A row into a loan's own accounts is a loan payment the server splits, or a posting it would refuse. */
export function useLoanPaymentExtension(options: UseLoanPaymentExtensionOptions): RegisteredExtension {
  const api = useApi()
  const queryClient = useQueryClient()
  const { t } = useI18n()
  const enabled = computed(() => canViewLoans(options.can) && options.ledgerId() !== undefined)

  const loansQuery = useQuery({
    queryKey: computed(() => loanKeys.list(options.ledgerId() ?? '')),
    queryFn: ({ signal }) => api.loans.list(options.ledgerId()!, signal),
    enabled,
    retry: false,
  })
  const owned = computed(() => loanAccountsById(enabled.value ? (loansQuery.data.value ?? []) : []))

  return defineEntryExtension<LoanRowMatch, LoanPaymentDraft>({
    id: 'loan-payment',
    detect: (view) => {
      const fromLoan = view.row.accountId === undefined ? undefined : owned.value.get(view.row.accountId)
      if (fromLoan !== undefined) {
        return {
          key: `borrowing:${fromLoan.loan.id}`,
          label: t('loans.extension.borrowingLabel', { name: fromLoan.loan.name }),
          mode: 'borrowing',
          loan: fromLoan.loan,
        }
      }
      const into = view.row.splits
        .map((split) => (split.targetId === undefined ? undefined : owned.value.get(split.targetId)))
        .find((found) => found !== undefined)
      if (into === undefined) return undefined
      const mode = into.role === 'interestExpense' ? 'interest' : 'payment'
      return {
        key: `${mode}:${into.loan.id}`,
        label: t(mode === 'interest' ? 'loans.extension.interestLabel' : 'loans.extension.paymentLabel', {
          name: into.loan.name,
        }),
        mode,
        loan: into.loan,
      }
    },
    carries: new Set(),
    initialDraft: () => ({ posted: undefined }),
    problems: (match, _draft, view) => {
      if (match.mode === 'borrowing') {
        return [{ severity: 'blocked', message: t('loans.extension.borrowingBlocked') }]
      }
      if (match.mode === 'interest') {
        return [{ severity: 'blocked', message: t('loans.extension.interestBlocked') }]
      }
      if (view.currency !== undefined && view.currency !== match.loan.currency) {
        return [
          {
            severity: 'needs-details',
            message: t('loans.extension.currencyMismatch', { currency: match.loan.currency }),
          },
        ]
      }
      return []
    },
    owns: (target) => owned.value.has(target.id),
    optionHint: (target) => {
      const found = owned.value.get(target.id)
      return found === undefined || found.role === 'interestExpense' ? undefined : t('loans.extension.hint')
    },
    detail: LoanPaymentRowDetail,
    placement: 'inline',
    submit: {
      shape: 'command',
      run: async (match, _draft, view) => {
        const ledgerId = options.ledgerId()!
        const posted = await api.loans.recordPayment(ledgerId, match.loan.id, {
          amount: view.total!,
          date: view.row.date,
          sourceAccountId: view.row.accountId!,
        })
        for (const queryKey of [
          ...loanKeys.invalidatedByWrite(ledgerId),
          ...entryKeys.invalidatedByWrite(ledgerId),
        ]) {
          void queryClient.invalidateQueries({ queryKey })
        }
        return { posted }
      },
    },
  })
}
